// OAuth Clients Management Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class OAuthClients extends HTMLElement {
  constructor() {
    super();
    this.clients = [];
    this.isLoadingClients = false;
    this.showAddForm = false;
    this.editingClient = null;
    this.newClient = {
      name: '',
      redirect_uris: [''],
      scopes: ['profile', 'email']
    };
    this.actionHandler = null;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.loadClients();
  }

  disconnectedCallback() {
    if (this.actionHandler) {
      this.removeEventListener('click', this.actionHandler);
    }
  }

  render() {
    this.innerHTML = `
      <div class="oauth-clients">
        <div class="clients-header">
          ${renderPageTitle('🔐', 'OAuth Clients')}
          <p>Manage OAuth2 client applications for SSO integration</p>
        </div>

        <div class="clients-actions">
          <button class="btn btn-primary" id="addClientBtn">
            + Create OAuth Client
          </button>
        </div>

        ${this.renderAddForm()}
        ${this.renderEditForm()}

        <div class="clients-content">
          ${this.renderContent()}
        </div>
      </div>
    `;

  }

  attachEventListeners() {
    this.actionHandler = (e) => {
      // Use closest() to handle clicks on child elements (like text nodes inside buttons)
      // For ID selector, check if target is the element or find it via closest
      const clickedElement = e.target.nodeType === Node.TEXT_NODE ? e.target.parentElement : e.target;
      const addBtn = clickedElement?.id === 'addClientBtn' || clickedElement?.closest('#addClientBtn');
      const editBtn = clickedElement?.closest('.btn-edit');
      const deleteBtn = clickedElement?.closest('.btn-delete');
      const copySecretBtn = clickedElement?.closest('.btn-copy-secret');
      const addUriBtn = clickedElement?.closest('.btn-add-uri');
      const removeUriBtn = clickedElement?.closest('.btn-remove-uri');
      const saveBtn = clickedElement?.closest('.btn-save');
      const cancelBtn = clickedElement?.closest('.btn-cancel');

      if (addBtn) {
        this.showAddForm = !this.showAddForm;
        this.editingClient = null;
        this.render();
      } else if (editBtn) {
        const clientId = parseInt(editBtn.dataset.clientId);
        this.editClient(clientId);
      } else if (deleteBtn) {
        const clientId = parseInt(deleteBtn.dataset.clientId);
        this.deleteClient(clientId);
      } else if (copySecretBtn) {
        const secret = copySecretBtn.dataset.secret;
        this.copyToClipboard(secret);
      } else if (addUriBtn) {
        this.addRedirectUri();
      } else if (removeUriBtn) {
        const index = parseInt(removeUriBtn.dataset.index);
        this.removeRedirectUri(index);
      } else if (saveBtn) {
        if (this.editingClient) {
          this.updateClient();
        } else {
          this.createClient();
        }
      } else if (cancelBtn) {
        this.cancelForm();
      }
    };

    // Attach to the component itself so it persists across re-renders
    // Remove any existing listener first to avoid duplicates
    this.removeEventListener('click', this.actionHandler);
    this.addEventListener('click', this.actionHandler);

    // Handle form inputs
    const nameInput = this.querySelector('#clientName');
    if (nameInput) {
      nameInput.addEventListener('input', (e) => {
        if (this.editingClient) {
          this.editingClient.name = e.target.value;
        } else {
          this.newClient.name = e.target.value;
        }
      });
    }

    // Handle redirect URI inputs
    this.querySelectorAll('.redirect-uri-input').forEach((input, index) => {
      input.addEventListener('input', (e) => {
        if (this.editingClient) {
          this.editingClient.redirect_uris[index] = e.target.value;
        } else {
          this.newClient.redirect_uris[index] = e.target.value;
        }
      });
    });

    // Handle scope checkboxes
    this.querySelectorAll('.scope-checkbox').forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        const scope = e.target.value;
        const checked = e.target.checked;

        if (this.editingClient) {
          if (checked && !this.editingClient.scopes.includes(scope)) {
            this.editingClient.scopes.push(scope);
          } else if (!checked) {
            this.editingClient.scopes = this.editingClient.scopes.filter(s => s !== scope);
          }
        } else {
          if (checked && !this.newClient.scopes.includes(scope)) {
            this.newClient.scopes.push(scope);
          } else if (!checked) {
            this.newClient.scopes = this.newClient.scopes.filter(s => s !== scope);
          }
        }
      });
    });
  }

  renderContent() {
    if (this.isLoadingClients) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading OAuth clients...</p>
        </div>
      `;
    }

    if (this.clients.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-icon">🔐</div>
          <h3>No OAuth Clients</h3>
          <p>Create your first OAuth client to enable SSO integration</p>
        </div>
      `;
    }

    return `
      <div class="clients-list">
        ${this.clients.map(client => this.renderClient(client)).join('')}
      </div>
    `;
  }

  renderClient(client) {
    const isRevoked = client.revoked;
    const createdDate = new Date(client.created_at).toLocaleDateString();

    return `
      <div class="client-card ${isRevoked ? 'revoked' : ''}">
        <div class="client-header">
          <div class="client-info">
            <h3>${escapeHtml(client.name)}</h3>
            <p class="client-id">Client ID: <code>${escapeHtml(client.client_id)}</code></p>
            <p class="client-meta">Created: ${createdDate}</p>
            ${isRevoked ? '<span class="revoked-badge">Revoked</span>' : ''}
          </div>
          <div class="client-actions">
            ${!isRevoked ? `
              <button class="btn btn-secondary btn-edit" data-client-id="${client.id}">
                Edit
              </button>
            ` : ''}
            <button class="btn btn-danger btn-delete" data-client-id="${client.id}">
              ${isRevoked ? 'Delete' : 'Revoke'}
            </button>
          </div>
        </div>
        <div class="client-details">
          <div class="detail-group">
            <label>Redirect URIs:</label>
            <ul class="uri-list">
              ${client.redirect_uris.map(uri => `
                <li><code>${escapeHtml(uri)}</code></li>
              `).join('')}
            </ul>
          </div>
          <div class="detail-group">
            <label>Scopes:</label>
            <div class="scopes-list">
              ${client.scopes.map(scope => `
                <span class="scope-badge">${escapeHtml(scope)}</span>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderAddForm() {
    if (!this.showAddForm || this.editingClient) {
      return '';
    }

    return `
      <div class="add-form">
        <h3>Create New OAuth Client</h3>
        <form id="addClientForm">
          <div class="form-group">
            <label for="clientName">Client Name *</label>
            <input 
              type="text" 
              id="clientName" 
              class="form-input" 
              placeholder="e.g., FreeScout"
              value="${escapeHtml(this.newClient.name)}"
              required
            />
          </div>

          <div class="form-group">
            <label>Redirect URIs *</label>
            <div id="redirectUrisContainer">
              ${this.newClient.redirect_uris.map((uri, index) => `
                <div class="uri-input-group">
                  <input 
                    type="url" 
                    class="form-input redirect-uri-input" 
                    placeholder="https://example.com/callback"
                    value="${escapeHtml(uri)}"
                    required
                  />
                  ${this.newClient.redirect_uris.length > 1 ? `
                    <button type="button" class="btn btn-danger btn-remove-uri" data-index="${index}">
                      Remove
                    </button>
                  ` : ''}
                </div>
              `).join('')}
            </div>
            <button type="button" class="btn btn-secondary btn-add-uri">
              + Add Redirect URI
            </button>
          </div>

          <div class="form-group">
            <label>Scopes</label>
            <div class="scopes-checkboxes">
              ${['profile', 'email', 'read', 'write'].map(scope => `
                <label class="checkbox-label">
                  <input 
                    type="checkbox" 
                    class="scope-checkbox" 
                    value="${scope}"
                    ${this.newClient.scopes.includes(scope) ? 'checked' : ''}
                  />
                  <span>${scope}</span>
                </label>
              `).join('')}
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-primary btn-save">Create Client</button>
            <button type="button" class="btn btn-secondary btn-cancel">Cancel</button>
          </div>
        </form>
      </div>
    `;
  }

  renderEditForm() {
    if (!this.editingClient) {
      return '';
    }

    return `
      <div class="edit-form">
        <h3>Edit OAuth Client</h3>
        <form id="editClientForm">
          <div class="form-group">
            <label>Client ID</label>
            <input 
              type="text" 
              class="form-input" 
              value="${escapeHtml(this.editingClient.client_id)}"
              disabled
            />
            <p class="form-help">Client ID cannot be changed</p>
          </div>

          <div class="form-group">
            <label for="editClientName">Client Name *</label>
            <input 
              type="text" 
              id="editClientName" 
              class="form-input" 
              value="${escapeHtml(this.editingClient.name)}"
              required
            />
          </div>

          <div class="form-group">
            <label>Redirect URIs *</label>
            <div id="editRedirectUrisContainer">
              ${this.editingClient.redirect_uris.map((uri, index) => `
                <div class="uri-input-group">
                  <input 
                    type="url" 
                    class="form-input redirect-uri-input" 
                    placeholder="https://example.com/callback"
                    value="${escapeHtml(uri)}"
                    required
                  />
                  ${this.editingClient.redirect_uris.length > 1 ? `
                    <button type="button" class="btn btn-danger btn-remove-uri" data-index="${index}">
                      Remove
                    </button>
                  ` : ''}
                </div>
              `).join('')}
            </div>
            <button type="button" class="btn btn-secondary btn-add-uri">
              + Add Redirect URI
            </button>
          </div>

          <div class="form-group">
            <label>Scopes</label>
            <div class="scopes-checkboxes">
              ${['profile', 'email', 'read', 'write'].map(scope => `
                <label class="checkbox-label">
                  <input 
                    type="checkbox" 
                    class="scope-checkbox" 
                    value="${scope}"
                    ${this.editingClient.scopes.includes(scope) ? 'checked' : ''}
                  />
                  <span>${scope}</span>
                </label>
              `).join('')}
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-primary btn-save">Save Changes</button>
            <button type="button" class="btn btn-secondary btn-cancel">Cancel</button>
          </div>
        </form>
      </div>
    `;
  }

  async loadClients() {
    if (this.isLoadingClients) {
      return;
    }

    this.isLoadingClients = true;
    this.render();

    try {
      const response = await fetch('/api/admin/oauth-clients', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load OAuth clients');
      }

      const data = await response.json();
      this.clients = data.clients || [];
    } catch (error) {
      window.logger?.error('Error loading OAuth clients:', error);
      this.clients = [];
    } finally {
      this.isLoadingClients = false;
      this.render();
    }
  }

  async createClient() {
    // Read values directly from the DOM instead of relying on event listeners
    const nameInput = this.querySelector('#clientName');
    const name = nameInput ? nameInput.value.trim() : this.newClient.name.trim();

    // Read redirect URIs from all input fields
    const redirectUriInputs = this.querySelectorAll('.redirect-uri-input');
    const redirectUris = Array.from(redirectUriInputs)
      .map(input => input.value.trim())
      .filter(uri => uri.length > 0);

    if (!name || redirectUris.length === 0) {
      alert('Please fill in all required fields');
      return;
    }

    // Read scopes from checkboxes
    const scopeCheckboxes = this.querySelectorAll('.scope-checkbox');
    const scopes = Array.from(scopeCheckboxes)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    try {
      const response = await fetch('/api/admin/oauth-clients', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name,
          redirect_uris: redirectUris,
          scopes: scopes
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create OAuth client');
      }

      const client = await response.json();

      // Show client secret in a modal/alert (only shown once)
      const secretMessage = `OAuth Client created successfully!\n\nClient ID: ${client.client_id}\nClient Secret: ${client.client_secret}\n\n⚠️ IMPORTANT: Save this client secret now. It will not be shown again!`;
      alert(secretMessage);

      // Reset form
      this.showAddForm = false;
      this.newClient = {
        name: '',
        redirect_uris: [''],
        scopes: ['profile', 'email']
      };

      // Reload clients
      await this.loadClients();
    } catch (error) {
      window.logger?.error('Error creating OAuth client:', error);
      alert(`Error: ${error.message}`);
    }
  }

  editClient(clientId) {
    const client = this.clients.find(c => c.id === clientId);
    if (!client) {
      return;
    }

    this.editingClient = {
      id: client.id,
      client_id: client.client_id,
      name: client.name,
      redirect_uris: [...client.redirect_uris],
      scopes: [...client.scopes]
    };
    this.showAddForm = false;
    this.render();
  }

  async updateClient() {
    // Read values directly from the DOM instead of relying on event listeners
    // This ensures we get the current form values even after re-renders
    const nameInput = this.querySelector('#editClientName');
    const name = nameInput ? nameInput.value.trim() : this.editingClient.name.trim();

    // Read redirect URIs from all input fields
    const redirectUriInputs = this.querySelectorAll('.redirect-uri-input');
    const redirectUris = Array.from(redirectUriInputs)
      .map(input => input.value.trim())
      .filter(uri => uri.length > 0);

    if (!name || redirectUris.length === 0) {
      alert('Please fill in all required fields');
      return;
    }

    // Read scopes from checkboxes
    const scopeCheckboxes = this.querySelectorAll('.scope-checkbox');
    const scopes = Array.from(scopeCheckboxes)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    try {
      const response = await fetch(`/api/admin/oauth-clients/${this.editingClient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name,
          redirect_uris: redirectUris,
          scopes: scopes
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update OAuth client');
      }

      // Reset form
      this.editingClient = null;
      this.showAddForm = false;

      // Reload clients
      await this.loadClients();
    } catch (error) {
      window.logger?.error('Error updating OAuth client:', error);
      alert(`Error: ${error.message}`);
    }
  }

  async deleteClient(clientId) {
    const client = this.clients.find(c => c.id === clientId);
    if (!client) {
      return;
    }

    const action = client.revoked ? 'permanently delete' : 'revoke';
    if (!confirm(`Are you sure you want to ${action} "${client.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/oauth-clients/${clientId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete OAuth client');
      }

      // Reload clients
      await this.loadClients();
    } catch (error) {
      window.logger?.error('Error deleting OAuth client:', error);
      alert(`Error: ${error.message}`);
    }
  }

  addRedirectUri() {
    // Read current form values from DOM before re-rendering
    this.syncFormDataFromDOM();

    if (this.editingClient) {
      this.editingClient.redirect_uris.push('');
    } else {
      this.newClient.redirect_uris.push('');
    }
    this.render();
  }

  removeRedirectUri(index) {
    // Read current form values from DOM before re-rendering
    this.syncFormDataFromDOM();

    if (this.editingClient) {
      this.editingClient.redirect_uris.splice(index, 1);
    } else {
      this.newClient.redirect_uris.splice(index, 1);
    }
    this.render();
  }

  syncFormDataFromDOM() {
    // Sync form data from DOM to component state before re-rendering
    if (this.editingClient) {
      // Sync edit form
      const nameInput = this.querySelector('#editClientName');
      if (nameInput) {
        this.editingClient.name = nameInput.value;
      }

      const redirectUriInputs = this.querySelectorAll('.redirect-uri-input');
      this.editingClient.redirect_uris = Array.from(redirectUriInputs)
        .map(input => input.value.trim());

      const scopeCheckboxes = this.querySelectorAll('.scope-checkbox');
      this.editingClient.scopes = Array.from(scopeCheckboxes)
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.value);
    } else if (this.showAddForm) {
      // Sync add form
      const nameInput = this.querySelector('#clientName');
      if (nameInput) {
        this.newClient.name = nameInput.value;
      }

      const redirectUriInputs = this.querySelectorAll('.redirect-uri-input');
      this.newClient.redirect_uris = Array.from(redirectUriInputs)
        .map(input => input.value.trim());

      const scopeCheckboxes = this.querySelectorAll('.scope-checkbox');
      this.newClient.scopes = Array.from(scopeCheckboxes)
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.value);
    }
  }

  cancelForm() {
    this.showAddForm = false;
    this.editingClient = null;
    this.newClient = {
      name: '',
      redirect_uris: [''],
      scopes: ['profile', 'email']
    };
    this.render();
  }

  copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(err => {
      window.logger?.error('Failed to copy to clipboard:', err);
    });
  }

}

customElements.define('oauth-clients', OAuthClients);

